import { t } from '@lingui/macro';
import { subDays, subMonths, subWeeks } from 'date-fns';
import { GraphIntervalType } from './MainGraph';
import { RangeType } from './Range';
import { PMZoneUnitsState } from './Preferences';

export interface RangePeriodModel {
  range: [Date, Date];
}

// shared context
export interface SharedContextModel extends RangePeriodModel {
  pageContext: string;
  refreshButton: RefreshButtonModel;
  showAllocatedSites: boolean;
}

export interface RefreshButtonModel {
  show: boolean;
  disabled: boolean;
  onClick: () => void;
}

// main graph context
export interface GraphContextModel extends MainGraphContextModel, MaintenanceGraphContextModel {
  graphPage: GraphPage;
}

export interface MainGraphContextModel extends PMZoneUnitsState {
  interval: GraphIntervalType;
}

export interface MaintenanceGraphContextModel {
  maintenanceSiteIds: number[];
  maintenanceTimeRange: RangeType;
}

export enum GraphPage {
  Main,
  Maintenance,
}

// logs context
export interface LogsContextModel {
  includeDeviceCommands: boolean;
  activeCommands: boolean;
  showFilter: LogsShowFilterType;
}

export const enum LogsShowFilterType {
  CurrentPMZ = 'CurrentPMZ',
  CurrentUser = 'CurrentUser',
}

export const LogsShowFilterOptionsFn = (): Record<LogsShowFilterType, string> => ({
  [LogsShowFilterType.CurrentPMZ]: t`Current PMZ`,
  [LogsShowFilterType.CurrentUser]: t`Current User`,
});

// alarm context
export interface AlarmContextModel {
  from: AlarmFromDatesType;
  acknowledge: AcknowledgeFilterType;
  showSystemAlarms: boolean;
}

export enum AlarmFromDatesType {
  From1Day = 'From1Day',
  From1Week = 'From1Week',
  From1Month = 'From1Month',
  From3Months = 'From3Months',
}

export enum AcknowledgeFilterType {
  All = 'All',
  NotAcknowledged = 'NotAcknowledged',
}

export const AlarmFormDatesOptionsFn = (): Record<AlarmFromDatesType, { date: Date; label: string }> => ({
  [AlarmFromDatesType.From1Day]: {
    date: subDays(new Date(), 1),
    label: t({ message: 'Show alarms 1 day old', id: '[AlarmList.From1Day]' }),
  },
  [AlarmFromDatesType.From1Week]: {
    date: subWeeks(new Date(), 1),
    label: t({ message: 'Show alarms 1 week old', id: '[AlarmList.From1Week]' }),
  },
  [AlarmFromDatesType.From1Month]: {
    date: subMonths(new Date(), 1),
    label: t({ message: 'Show alarms 1 month old', id: '[AlarmList.From1Month]' }),
  },
  [AlarmFromDatesType.From3Months]: {
    date: subMonths(new Date(), 3),
    label: t({ message: 'Show alarms 3 months old', id: '[AlarmList.From3Months]' }),
  },
});

export const AcknowledgeFilterOptionsFn = (): Record<AcknowledgeFilterType, string> => ({
  [AcknowledgeFilterType.All]: t({
    message: 'Show new and acknowledged alarms',
    id: '[AlarmList.All]',
  }),
  [AcknowledgeFilterType.NotAcknowledged]: t({
    message: 'Show new alarms only',
    id: '[AlarmList.NotAcknowledged]',
  }),
});

//
// filters

// graph
export interface MainGraphFiltersStateModel extends MainGraphContextModel, RangePeriodModel {}

// logs
export interface LogsFiltersStateModel extends LogsContextModel, RangePeriodModel {}

// alarms
export interface AlarmsFiltersStateModel extends AlarmContextModel {}
